import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { SingleDonutGraphConfigurations, SingleDonutGraphValue } from '../../tools/single-donut-with-image/single-donut-with-image-models';
import { KlaHttpService } from '../../tools/services/kla.http.service';
import * as d3 from 'd3';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kla-single-donut',
  templateUrl: './single-donut.component.html',
  styleUrls: ['./single-donut.component.scss'],
  standalone: true,
  imports: [ CommonModule ]
})
export class SingleDonutComponent implements OnInit {
  @Input()
  public config: SingleDonutGraphConfigurations;

  @Input()
  public chartAltText: string;

  public imagePath: string;
  private chartData: Array<any> = []; // Za čuvanje podataka

  @ViewChild('singleDonut', {static: true}) private chartContainer: ElementRef;

  constructor(private httpService: KlaHttpService) {
  }

  onContainerKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const firstSegment = this.chartContainer.nativeElement.querySelector('.arc-group[tabindex="0"]');
      if (firstSegment) { firstSegment.focus(); }
    }
  }

  ngOnInit() {
    if (this.config.data) {
      this.createChart();
    }
  }

  @Input('width')
  set setWidth(width: number) {
    if (!this.config) { return; }
    this.config.width = width;
    this.ngOnInit();
  }

  createViewPort(width: number, height: number) {
    d3.select(this.chartContainer.nativeElement).selectAll('.single-donut-graph').remove();

    return d3.select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('class', 'pie single-donut-graph')
      .attr('width', width)
      .attr('height', width)
      .attr('viewBox', '0 0 408 408')
      .attr('focusable', 'false')
      .append('g')
      .attr('transform', 'translate(' + (10 + (width / 2)) + ',' + (10 + (height / 2)) + ')');
  }

  createChart() {
    const data: Array<SingleDonutGraphValue> = []
    this.config.data.forEach((item: SingleDonutGraphValue) => {
      const imageConfig = {
        id: item.icon ? item.icon.id : '',
        width: 300,
        height: undefined,
        crop_config: item.icon ? item.icon.crop_config : {}
      }

      data.push({
        ...item,
        url: item.icon?.crop_config ? this.httpService.getCroppedImagePath(imageConfig) : item.icon?.url
      })
    })

    this.chartData = data;

    const width = this.config.width;
    const height = this.config.height;
    const thickness = this.config.thickness;

    const radius = Math.min(width, height) / 2;

    const pie: any = d3.pie<any>()
      .value(function (d) {
        return d.value || 1;
      })
      .sort(null);

    const view = this.createViewPort(width, height);

    const imageLayer = view.append('g')
    .attr('class', 'image-layer');
    const maskLayer  = view.append('g').attr('class', 'mask-layer'); 
    const arcLayer = view.append('g').attr('class', 'arcs');
    const labelLayer = view.append('g').attr('class', 'labels');

    const cordinates = this.createOuterDonut(arcLayer, pie(data), radius, thickness);
    
    const mask = this.createMask(cordinates);

    mask.forEach(item => {
      this.hideImage(maskLayer, item, radius, thickness);
    });

    const imageConfig = {
      id: this.config.image ? this.config.image.id : '',
      width: radius * 2,
      height: undefined,
      crop_config: this.config.image ? this.config.image.crop_config : {}
    }

    this.imagePath = this.httpService.getCroppedImagePath(imageConfig);
    this.addImage(imageLayer, radius * 2, height, thickness);

    this.addLabelsAndImages(labelLayer, pie(data), radius, thickness);    
  }

  createOuterDonut(layer, data, radius, thickness) {
    const cordinates = [];
    const self = this;
    const arcGroups: any[] = [];

    const arc = d3.arc<any>()
      .innerRadius(radius - thickness)
      .outerRadius(radius);

    const focusSegment = (targetGroup) => {
      arcGroups.forEach(g => g.attr('tabindex', '-1'));
      targetGroup.attr('tabindex', '0');
      targetGroup.raise();
      targetGroup.node().focus();
    };

    const group = layer.selectAll('.arc-group')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'arc-group')
      .style('overflow', 'hidden');

    group.each(function(d, i) {
      arcGroups.push(d3.select(this));
    });

    group
      .attr('tabindex', (d, i) => i === 0 ? '0' : '-1')
      .attr('aria-label', d => {
        const valueColumnName = d.data?.valueColumnName || '';
        return valueColumnName
          ? `${d.data.title}: ${valueColumnName} ${d.data.value}`
          : `${d.data.title}: ${d.data.value}`;
      })
      .attr('aria-roledescription', 'chart segment')
      .style('cursor', 'pointer')
      .on('focus', function(d) {
        const el = this;
        setTimeout(() => {
          if (el.parentNode && (el.parentNode as Element).lastElementChild !== el) {
            d3.select(el).raise();
            (el as SVGElement).focus();
          }
        }, 50);
        const rect = (el as Element).getBoundingClientRect();
        const fakeEvent = { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 };
        self.showSegmentData(d, fakeEvent);
      })
      .on('blur', function(d) {
        self.hideSegmentData(d);
      })
      .on('keydown', function(d) {
        const e = d3.event;
        const idx = arcGroups.findIndex(g => g.node() === this);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          focusSegment(arcGroups[(idx + 1) % arcGroups.length]);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          focusSegment(arcGroups[(idx - 1 + arcGroups.length) % arcGroups.length]);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          arcGroups.forEach(g => g.attr('tabindex', '-1'));
          arcGroups[0].attr('tabindex', '0');
          self.chartContainer.nativeElement.focus();
        }
      });

    group.append('path')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .each((d, i) => {
        cordinates.push(d);
        d.index = i;
      })
      .on('mouseenter', function(d) {
        const realEvent = (d3.event as MouseEvent);
        self.showSegmentData(d, realEvent);
      })
      .on('mousemove', function(d) {
        const realEvent = (d3.event as MouseEvent);
        self.updateTooltipPosition(d, realEvent);
      })
      .on('mouseleave', function(d) {
        self.hideSegmentData(d);
      });

    return cordinates;
  }

  addLabelsAndImages(layer, data, radius, thickness) {
    const arc = d3.arc<any>()
      .innerRadius(radius - thickness)
      .outerRadius(radius);

    const defs = layer.select('defs').empty() ? 
      layer.append('defs') : layer.select('defs');

    // const labelGroup = layer.selectAll('.label-group')
    //   .data(data)
    //   .enter()
    //   .append('g')
    //   .attr('class', 'label-group')
    //   .attr('transform', d => {
    //     const [x, y] = arc.centroid(d);
    //     return `translate(${x}, ${y})`;
    //   });
    //   console.log('labelGroup', labelGroup);

    // labelGroup.append('text')
    //   .text(d => d.data.title)
    //   .attr('y', -15)
    //   .attr('text-anchor', 'middle')
    //   .attr('fill', '#000')
    //   .style('font-size', '14px')
    //   .style('font-weight', 'bold');
    

    // labelGroup.append('text')
    //   .text(d => d.data.subTitle || d.data.description)
    //   .attr('y', 0)
    //   .attr('text-anchor', 'middle')
    //   .attr('fill', '#666')
    //   .style('font-size', '12px');

    // const imageSize = thickness/2;
    // console.log('imageSize', imageSize);
    // console.log('thickness', thickness);
    // labelGroup.append('image')
    //   .attr('xlink:href', d => d.data.url)
    //   .attr('width', imageSize)
    //   .attr('height', imageSize)
    //   .attr('x', -imageSize/2)
    //   .attr('y', d => d.data.subTitle || d.data.description ? 5 : -10);

    const labelGroup = layer.selectAll('.label-group')
    .data(data)
    .enter()
    .append('g')
    .attr('class', 'label-group')
    .attr('transform', d => {
      const [x, y] = arc.centroid(d);
      return `translate(${x}, ${y})`;
    });

  const baseImageSize = thickness*2.5/3;

    labelGroup.each(function(d, i) {
      const [x, y] = arc.centroid(d);
      const isTopHalf = y < 0;
      const currentGroup = d3.select(this);

      const segmentAngle = d.endAngle - d.startAngle;
      const segmentPercentage = segmentAngle / (2 * Math.PI);
      const sizeFactor = Math.min(1, Math.max(0, segmentPercentage * 8));
      const imageSize = baseImageSize * sizeFactor;

      currentGroup.attr('id', `segment-${i}`);
      const imageHeight = imageSize;
      const textHeight = 13;
      const descHeight = 10;
      const padding = 2;
      const hasDescription = d.data.subTitle || d.data.description;
      const totalHeight = imageHeight + padding + textHeight +
      (hasDescription ? padding + descHeight : 0);
      const offsetY = -totalHeight / 2;

      const clipId = `circle-clip-${i}`;
      defs.append('clipPath')
        .attr('id', clipId)
        .append('circle')
        .attr('id', `clip-circle-${i}`)
        .attr('r', imageSize/2)
        .attr('cx', 0)
        .attr('cy', 0);

      currentGroup.append('image')
        .attr('id', `segment-image-${i}`)
        .attr('xlink:href', d.data.url)
        .attr('width', imageSize)
        .attr('height', imageSize)
        .attr('x', -imageSize/2)
        .attr('y', -imageSize/2)
        .attr('data-original-size', imageSize)
        .attr('clip-path', `url(#${clipId})`)
        .attr('preserveAspectRatio', 'xMidYMid slice')
        .style('pointer-events', 'none');

      if (isTopHalf) {
        const titleY = -imageSize/2 - padding;
        currentGroup.append('text')
          .attr('id', `title-${i}`)
          .text(d.data.title)
          .attr('y', titleY)
          .attr('data-original-y', titleY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#000')
          .style('font-size', '13px')
          .style('font-weight', 'bold')
          .style('text-shadow', '2px 2px 4px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)')
          .style('pointer-events', 'none');

        if (hasDescription) {
          const descY = -imageSize/2 - padding - descHeight;
          currentGroup.append('text')
            .attr('id', `desc-${i}`)
            .text(d.data.subTitle || d.data.description)
            .attr('y', descY)
            .attr('data-original-y', descY)
            .attr('text-anchor', 'middle')
            .attr('fill', '#333')
            .style('font-size', '10px')
            .style('font-weight', '600')
            .style('text-shadow', '1px 1px 3px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)')
            .style('pointer-events', 'none');
        }


      } else {
        const titleY = imageSize/2 + padding + textHeight;
        currentGroup.append('text')
          .attr('id', `title-${i}`)
          .text(d.data.title)
          .attr('y', titleY)
          .attr('data-original-y', titleY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#000')
          .style('font-size', '13px')
          .style('font-weight', 'bold')
          .style('text-shadow', '2px 2px 4px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)')
          .style('pointer-events', 'none');

        if (hasDescription) {
          const descY = imageSize/2 + padding + textHeight + padding + descHeight;
          currentGroup.append('text')
            .attr('id', `desc-${i}`)
            .text(d.data.subTitle || d.data.description)
            .attr('y', descY)
            .attr('data-original-y', descY)
            .attr('text-anchor', 'middle')
            .attr('fill', '#333')
            .style('font-size', '10px')
            .style('font-weight', '600')
            .style('text-shadow', '1px 1px 3px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)')
            .style('pointer-events', 'none');
        }
      }
    });

  }

  hideImage(view, mask, radius, thickness) {
    const arcline = d3.arc<any>()
      .innerRadius(radius - thickness)
      .outerRadius(radius)
      .startAngle(mask.startAngle)
      .endAngle(mask.endAngle);

    view.append('g')
      .attr('class', 'selected-line')
      .append('path')
      .attr('d', arcline)
      .attr('fill', mask.color);
  }

  createMask(data) {
    const startAngle = Math.PI / 2;
    const endAngle = startAngle * 3;
    const maskData = [];
    data.forEach(element => {
      let startCustom = 0, endCustom = 0;
      if (element.startAngle < startAngle && element.endAngle > startAngle) {
        startCustom = startAngle;
      }
      if (element.startAngle >= startAngle && element.startAngle < endAngle) {
        startCustom = element.startAngle;
      }
      if (element.endAngle > endAngle && element.startAngle < endAngle) {
        endCustom = endAngle;
      }
      if (element.endAngle <= endAngle && element.endAngle > startAngle) {
        endCustom = element.endAngle;
      }
      if (startAngle != 0 && endAngle != 0) {
        maskData.push({
          startAngle: startCustom,
          endAngle: endCustom,
          color: element.data.color
        });
      }
    });
    return maskData;
  }

  addImage(layer, width, height, thickness) {
    const imageWidth = width-2*thickness;
    const imageY = -Math.abs(imageWidth / 2);
    const imageX = -Math.abs(imageWidth / 2);
    layer.append('image')
      .attr('id', 'bg')
      .attr('xlink:href', this.imagePath)
      .attr('x', imageX)
      .attr('y', imageY)
      .attr('width', imageWidth)
      .attr('height', imageWidth)
      .attr('preserveAspectRatio', 'xMidYMid slice');
  }

  showSegmentData(segment: any, event: any) {
    if (!segment) {
      return;
    }

    console.log('Segment data on hover:', segment.data);
    const value = segment.data?.value || segment.value || (segment.endAngle - segment.startAngle);
    const title = segment.data?.title || segment.title || 'Data';
    const color = segment.data?.color || '#000';
    const valueColumnName = segment.data?.valueColumnName || '';
    console.log('valueColumnName:', valueColumnName);
    const index = segment.index;

    const segmentImage = d3.select(`#segment-image-${index}`);
    const clipCircle = d3.select(`#clip-circle-${index}`);
    const titleText = d3.select(`#title-${index}`);
    const descText = d3.select(`#desc-${index}`);

    if (!segmentImage.empty()) {
      const originalSize = parseFloat(segmentImage.attr('data-original-size'));
      const hoverSize = this.config.thickness;
      const sizeDiff = (hoverSize - originalSize) / 2;

      segmentImage
        .transition()
        .duration(500)
        .attr('width', hoverSize)
        .attr('height', hoverSize)
        .attr('x', -hoverSize/2)
        .attr('y', -hoverSize/2);

      clipCircle
        .transition()
        .duration(500)
        .attr('r', hoverSize/2);

      if (!titleText.empty()) {
        const currentY = parseFloat(titleText.attr('y'));
        const isAbove = currentY < 0;
        const offset = isAbove ? -sizeDiff : sizeDiff;

        titleText
          .transition()
          .duration(500)
          .attr('y', currentY + offset);
      }

      if (!descText.empty()) {
        const currentY = parseFloat(descText.attr('y'));
        const isAbove = currentY < 0;
        const offset = isAbove ? -sizeDiff : sizeDiff;

        descText
          .transition()
          .duration(500)
          .attr('y', currentY + offset);
      }
    }

    let tooltip = d3.select('body').select('.d3-tooltip-hover');
    if (tooltip.empty()) {
      tooltip = d3.select('body')
        .append('div')
        .attr('class', 'd3-tooltip-hover')
        .style('position', 'fixed')
        .style('background', '#ffffff')
        .style('border-radius', '4px')
        .style('padding', '5px')
        .style('box-shadow', '0px 4px 12px rgba(0, 0, 0, 0.25)')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', '10000')
        .style('font-family', 'Arial, sans-serif')
        .style('white-space', 'nowrap');
    }

    const valueLabel = valueColumnName ? `${valueColumnName}: ` : '';

    tooltip.html(`
      <div style="color: #666; font-size: 11px; font-weight: normal; margin-bottom: 1px; line-height: 1;">${title}</div>
      <div style="display: flex; align-items: center; gap: 5px; line-height: 1;">
        <div style="width: 6px; height: 6px; border-radius: 50%; background: ${color};"></div>
        <span style="color: #666; font-size: 12px; font-weight: normal;">${valueLabel}</span><span style="color: #000; font-size: 12px; font-weight: bold;">${value}</span>
      </div>
    `);

    this.updateTooltipPosition(segment, event);

    tooltip
      .interrupt()
      .style('opacity', 1);
  }

  updateTooltipPosition(segment: any, event: any) {
    const tooltip = d3.select('body').select('.d3-tooltip-hover');

    if (!tooltip.empty() && event) {
      const offsetX = 15;
      const offsetY = -40;

      tooltip
        .style('left', (event.clientX + offsetX) + 'px')
        .style('top', (event.clientY + offsetY) + 'px');
    }
  }

  hideSegmentData(segment: any) {
    if (!segment) {
      return;
    }

    const index = segment.index;

    const segmentImage = d3.select(`#segment-image-${index}`);
    const clipCircle = d3.select(`#clip-circle-${index}`);
    const titleText = d3.select(`#title-${index}`);
    const descText = d3.select(`#desc-${index}`);

    if (!segmentImage.empty()) {
      const originalSize = parseFloat(segmentImage.attr('data-original-size'));

      segmentImage
        .transition()
        .duration(500)
        .attr('width', originalSize)
        .attr('height', originalSize)
        .attr('x', -originalSize/2)
        .attr('y', -originalSize/2);

      clipCircle
        .transition()
        .duration(500)
        .attr('r', originalSize/2);

      if (!titleText.empty()) {
        const originalY = parseFloat(titleText.attr('data-original-y'));

        titleText
          .transition()
          .duration(500)
          .attr('y', originalY);
      }

      if (!descText.empty()) {
        const originalY = parseFloat(descText.attr('data-original-y'));

        descText
          .transition()
          .duration(500)
          .attr('y', originalY);
      }
    }

    const tooltip = d3.select('body').select('.d3-tooltip-hover');
    if (!tooltip.empty()) {
      tooltip.transition()
        .duration(200)
        .style('opacity', 0);
    }
  }

}
